import { boundMethod } from 'autobind-decorator';
import type { AsyncStatus, NumberType } from 'lib-shared-legacy-types';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import type { Country } from 'hocs/countries/api/types';

import telephony from './api/telephony';
import type {
  PhoneNumbersGroup,
  PhoneNumbersSearch,
  PhoneNumbersSearchQuery,
  PhoneNumbersSearchResponse,
} from './api/types';

export interface WithPhoneSearchInjectedProps {
  phoneSearch: WithPhoneSearchState & WithPhoneSearchMethods;
}

interface WithPhoneSearchMethods {
  getItems: (
    params: PhoneNumbersSearchQuery,
    types: Array<NumberType>,
  ) => Promise<Array<PhoneNumberResponseWithDetails>>;
  nextPage: (type: NumberType) => Promise<PhoneNumberResponseWithDetails>;
  previousPage: (type: NumberType) => Promise<PhoneNumberResponseWithDetails>;
}

interface WithPhoneSearchState {
  items: ItemsByNumberType;
  getItemsAsyncStatus: AsyncStatus;
}

type ItemsByNumberType = {
  [Key in NumberType]?: {
    numbers: Array<PhoneNumbersSearch>;
    groups: Array<PhoneNumbersGroup>;
    country: Country | undefined;
    nextPagePath: string | null;
    previousPagePath: string | null;
  };
};

type PhoneNumberResponseWithDetails = PhoneNumbersSearchResponse & {
  type: NumberType;
  country: Country | undefined;
};

type WithPhoneSearchNotInjectedProps<T> = Omit<
  T,
  keyof WithPhoneSearchInjectedProps
>;

export const withPhoneSearch = <T extends WithPhoneSearchInjectedProps>(
  Component: ComponentType<T>,
) => {
  class WithPhoneSearch
    extends PureComponent<
      WithPhoneSearchNotInjectedProps<T>,
      WithPhoneSearchState
    >
    implements WithPhoneSearchMethods
  {
    state: WithPhoneSearchState = {
      items: {},
      getItemsAsyncStatus: 'succeeded',
    };

    memoizedGetInjectedProps = memoize(this.getInjectedProps).bind(this);

    @boundMethod
    getItems(
      params: Omit<PhoneNumbersSearchQuery, 'type'>,
      types: Array<NumberType>,
    ): Promise<Array<PhoneNumberResponseWithDetails>> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      const { items } = this.state;
      const { country } = params;

      const searchQueries = types.map((type) =>
        telephony.searchNumbers({ ...params, type }),
      );

      return Promise.all(searchQueries)
        .then((resp) =>
          resp.map((response, index) => ({
            ...response,
            /*
             * If we're requesting next page or previous page
             * then we won't be getting `country` from params,
             * therefor we take it from state if it's not in params.
             */
            country: country || items[types[index]]?.country,
            type: types[index],
          })),
        )
        .then(this.handleQueryResolve, this.handleQueryReject);
    }

    @boundMethod
    handleQueryResolve(response: Array<PhoneNumberResponseWithDetails>) {
      this.setState(({ items: stateItems }) => {
        const items = response.reduce<ItemsByNumberType>(
          (list, { data, meta, type, country }) => ({
            ...list,
            [type]: {
              country,
              groups: data.groups.groups,
              numbers: data.numbers,
              nextPagePath: meta.next_page,
              previousPagePath: meta.prev_page,
            },
          }),
          stateItems,
        );

        return {
          getItemsAsyncStatus: 'succeeded',
          items,
        };
      });

      return response;
    }

    @boundMethod
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleQueryReject(error: any) {
      this.setState({ getItemsAsyncStatus: 'failed' });

      return error;
    }

    @boundMethod
    nextPage(
      type: NumberType,
    ): Promise<PhoneNumberResponseWithDetails> | never {
      const { items } = this.state;
      const nextPagePath = items[type]?.nextPagePath;

      if (!nextPagePath) {
        throw new Error('Next page does not exist');
      }

      return this.getItems({ next_page: nextPagePath }, [type]).then(
        ([response]) => response,
      );
    }

    @boundMethod
    previousPage(
      type: NumberType,
    ): Promise<PhoneNumberResponseWithDetails> | never {
      const { items } = this.state;
      const previousPagePath = items[type]?.nextPagePath;

      if (!previousPagePath) {
        throw new Error('Next page does not exist');
      }

      return this.getItems({ prev_page: previousPagePath }, [type]).then(
        ([response]) => response,
      );
    }

    @boundMethod
    getInjectedProps(
      items: Readonly<WithPhoneSearchState>['items'],
      getItemsAsyncStatus: Readonly<WithPhoneSearchState>['getItemsAsyncStatus'],
    ): WithPhoneSearchInjectedProps['phoneSearch'] {
      return {
        items,
        getItemsAsyncStatus,
        getItems: this.getItems,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
      };
    }

    render() {
      const { items, getItemsAsyncStatus } = this.state;

      return (
        <Component
          {...(this.props as T)}
          phoneSearch={this.memoizedGetInjectedProps(
            items,
            getItemsAsyncStatus,
          )}
        />
      );
    }
  }

  return WithPhoneSearch;
};
